import { useState, useEffect, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useLandingStSettings } from '@/hooks/useLandingStSettings';

function InfoNote({ children }: { children: string }) {
  return (
    <div className="rounded-lg border border-secondary-200 bg-secondary-50/50 px-4 py-3">
      <p className="flex items-start gap-2 text-sm text-secondary-700">
        <i className="ri-information-line mt-0.5 shrink-0" />
        <span>{children}</span>
      </p>
    </div>
  );
}

function TestResult({ result }: { result: { success: boolean; message: string } | null }) {
  if (!result) return null;
  return (
    <div
      className={`flex items-start gap-2 rounded-md px-3.5 py-2.5 ${
        result.success
          ? 'bg-green-50 border border-green-200 text-green-700'
          : 'bg-red-50 border border-red-200 text-red-700'
      }`}
    >
      <i
        className={`${
          result.success ? 'ri-check-line' : 'ri-error-warning-line'
        } text-sm mt-0.5 shrink-0`}
      />
      <p className="text-sm">{result.message}</p>
    </div>
  );
}

export default function LandingStSettings() {
  const { settings, loading, error, updateSettings, refetch } = useLandingStSettings();

  // Product sync fields
  const [token, setToken] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [enabled, setEnabled] = useState(false);

  // Booking sync fields
  const [bookingToken, setBookingToken] = useState('');
  const [bookingEndpoint, setBookingEndpoint] = useState('');
  const [bookingEnabled, setBookingEnabled] = useState(false);

  const [saving, setSaving] = useState<'product' | 'booking' | null>(null);
  const [toast, setToast] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Test connection states
  const [testingProduct, setTestingProduct] = useState(false);
  const [productTestResult, setProductTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testingBooking, setTestingBooking] = useState(false);
  const [bookingTestResult, setBookingTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setToken(settings.webhook_token ?? '');
      setEndpoint(settings.endpoint_url ?? '');
      setEnabled(settings.enabled ?? false);
      setBookingToken(settings.booking_webhook_token ?? '');
      setBookingEndpoint(settings.booking_endpoint_url ?? '');
      setBookingEnabled(settings.booking_enabled ?? false);
    }
  }, [settings]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 3000);
  };

  const handleProductSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setProductTestResult(null);
    if (!token.trim()) {
      setFormError('Product webhook token is required.');
      return;
    }
    if (!endpoint.trim()) {
      setFormError('Product endpoint URL is required.');
      return;
    }
    setSaving('product');
    const result = await updateSettings({
      webhook_token: token.trim(),
      endpoint_url: endpoint.trim(),
      enabled,
    });
    setSaving(null);
    if (result.error) {
      setFormError(result.error);
      return;
    }
    showToast('Product sync settings saved successfully.');
    refetch();
  };

  const handleBookingSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setBookingTestResult(null);
    if (!bookingToken.trim()) {
      setFormError('Booking webhook token is required.');
      return;
    }
    if (!bookingEndpoint.trim()) {
      setFormError('Booking endpoint URL is required.');
      return;
    }
    setSaving('booking');
    const result = await updateSettings({
      booking_webhook_token: bookingToken.trim(),
      booking_endpoint_url: bookingEndpoint.trim(),
      booking_enabled: bookingEnabled,
    });
    setSaving(null);
    if (result.error) {
      setFormError(result.error);
      return;
    }
    showToast('Booking sync settings saved successfully.');
    refetch();
  };

  const runTestConnection = async (
    type: 'product' | 'booking',
    endpointUrl: string,
    tokenValue: string,
    setLoading: (v: boolean) => void,
    setResult: (r: { success: boolean; message: string } | null) => void,
  ) => {
    setFormError(null);
    setResult(null);
    if (!tokenValue.trim()) {
      setResult({ success: false, message: 'Please enter a webhook token before testing.' });
      return;
    }
    if (!endpointUrl.trim()) {
      setResult({ success: false, message: 'Please enter an endpoint URL before testing.' });
      return;
    }
    setLoading(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'vietnamtravel-test-landingst-connection',
        {
          body: { type, endpoint: endpointUrl.trim(), token: tokenValue.trim() },
        },
      );
      if (invokeError) throw invokeError;
      setResult({ success: data?.success ?? false, message: data?.message || 'Unknown response' });
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to test connection.',
      });
    } finally {
      setLoading(false);
    }
  };

  const testProductConnection = () => {
    runTestConnection('product', endpoint, token, setTestingProduct, setProductTestResult);
  };

  const testBookingConnection = () => {
    runTestConnection('booking', bookingEndpoint, bookingToken, setTestingBooking, setBookingTestResult);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 text-secondary-500">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <span className="text-sm">Loading landing.st settings...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">Something went wrong</h3>
        <p className="mt-1 text-sm text-secondary-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-lg font-bold text-secondary-900">Landing.st Sync</h2>
        <p className="text-sm text-secondary-500 mt-0.5">
          Configure separate webhook tokens and endpoints for product sync and booking sync.
          Each uses its own token — they are not shared.
        </p>
      </div>

      {toast && (
        <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
          <i className="ri-checkbox-circle-line text-base" />
          {toast}
        </div>
      )}

      {formError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-md px-3.5 py-2.5">
          <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 shrink-0" />
          <p className="text-sm text-red-600">{formError}</p>
        </div>
      )}

      {/* Product Sync */}
      <div className="bg-white rounded-lg border border-secondary-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <i className="ri-shopping-bag-3-line text-primary-500 text-lg" />
          <h3 className="font-heading text-base font-bold text-secondary-900">Product Sync</h3>
        </div>
        <p className="text-sm text-secondary-500 mb-5">
          Syncs tour products (name, price, variants) to landing.st when creating or updating tours.
        </p>

        <form onSubmit={handleProductSubmit} className="space-y-5">
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              Product Webhook Token <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={token}
              onChange={(e) => { setToken(e.target.value); setProductTestResult(null); }}
              placeholder="YOUR_PRODUCT_SOURCE_TOKEN"
              className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
            />
            <p className="mt-1 text-[11px] text-secondary-400">
              Copy from landing.st dashboard → Products → Webhook tab.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              Product Endpoint URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={endpoint}
              onChange={(e) => { setEndpoint(e.target.value); setProductTestResult(null); }}
              placeholder="https://your-project.supabase.co/functions/v1/landingapp-products-webhook"
              className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
            />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
            />
            <span className="text-sm text-secondary-700">Enable auto-sync when creating or updating tours</span>
          </label>

          <TestResult result={productTestResult} />

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving === 'product'}
              className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              {saving === 'product' ? (
                <i className="ri-loader-4-line animate-spin text-base" />
              ) : (
                <i className="ri-save-line text-base" />
              )}
              Save product settings
            </button>
            <button
              type="button"
              disabled={testingProduct}
              onClick={testProductConnection}
              className="inline-flex items-center justify-center gap-2 bg-secondary-100 hover:bg-secondary-200 disabled:opacity-60 text-secondary-700 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              {testingProduct ? (
                <i className="ri-loader-4-line animate-spin text-base" />
              ) : (
                <i className="ri-wifi-line text-base" />
              )}
              Test connection
            </button>
          </div>
        </form>
      </div>

      {/* Booking Sync */}
      <div className="bg-white rounded-lg border border-secondary-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <i className="ri-calendar-check-line text-primary-500 text-lg" />
          <h3 className="font-heading text-base font-bold text-secondary-900">Booking Sync</h3>
        </div>
        <p className="text-sm text-secondary-500 mb-5">
          Syncs customer bookings and trip inquiries to landing.st as orders.
        </p>

        <form onSubmit={handleBookingSubmit} className="space-y-5">
          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              Booking Webhook Token <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={bookingToken}
              onChange={(e) => { setBookingToken(e.target.value); setBookingTestResult(null); }}
              placeholder="YOUR_BOOKING_SOURCE_TOKEN"
              className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
            />
            <p className="mt-1 text-[11px] text-secondary-400">
              Copy from landing.st dashboard → Orders / Bookings → Webhook tab.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
              Booking Endpoint URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={bookingEndpoint}
              onChange={(e) => { setBookingEndpoint(e.target.value); setBookingTestResult(null); }}
              placeholder="https://your-project.supabase.co/functions/v1/landingapp-orders-webhook"
              className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
            />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={bookingEnabled}
              onChange={(e) => setBookingEnabled(e.target.checked)}
              className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
            />
            <span className="text-sm text-secondary-700">Enable auto-sync for new bookings and inquiries</span>
          </label>

          <TestResult result={bookingTestResult} />

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving === 'booking'}
              className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              {saving === 'booking' ? (
                <i className="ri-loader-4-line animate-spin text-base" />
              ) : (
                <i className="ri-save-line text-base" />
              )}
              Save booking settings
            </button>
            <button
              type="button"
              disabled={testingBooking}
              onClick={testBookingConnection}
              className="inline-flex items-center justify-center gap-2 bg-secondary-100 hover:bg-secondary-200 disabled:opacity-60 text-secondary-700 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              {testingBooking ? (
                <i className="ri-loader-4-line animate-spin text-base" />
              ) : (
                <i className="ri-wifi-line text-base" />
              )}
              Test connection
            </button>
          </div>
        </form>
      </div>

      <InfoNote>
        Product sync and booking sync use separate tokens and endpoints. Keep them apart — the
        product token authorizes pushing products, while the booking token authorizes pushing
        orders/bookings into landing.st. Use the Test connection button before saving to make sure
        the token and endpoint are valid.
      </InfoNote>
    </div>
  );
}